import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
      })
      .catch((err) => {
        /**
         * Only the server saying "no" ends a session. A request that never
         * reached it — offline, a dropped connection, or the browser aborting
         * it because the clinician navigated while it was in flight — used to
         * clear the token too, which logged people out at random during quick
         * navigation. The cached user stands until the API refuses it.
         */
        if (err?.status === 401 || err?.status === 403) {
          clearSession();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // `portal` says which sign-in page the credentials came from, so the API can
  // keep patients out of the staff portal and vice versa (issues 8232/8270).
  const login = useCallback(async (email, password, portal) => {
    const res = await api.login({ email, password, portal });
    // The account's role may require MFA — return the challenge instead of a
    // session so the caller can prompt for the verification code.
    // `enrolment` is present when the account has no authenticator app yet and
    // must set one up to finish signing in (register 8.14).
    if (res?.mfaRequired) {
      return { mfaRequired: true, mfaToken: res.mfaToken, email: res.email, enrolment: res.enrolment };
    }
    persistSession(res.user, res.token);
    setUser(res.user);
    return res.user;
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const { user: u, token } = await api.verifyMfa({ mfaToken, code });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  // Sign in with a session the caller already holds. The sign-in page verifies
  // an enrolment or a recovery code itself, because it has something to show
  // (the recovery codes) before the session takes over the screen.
  const adoptSession = useCallback((u, token) => {
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, name, password) => {
    const { user: u, token } = await api.register({ email, name, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const value = { user, loading, isAuthenticated: !!user, login, verifyMfa, adoptSession, register, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
